import { useTranslation } from 'react-i18next';
import { BookOpen, X } from 'lucide-react';

interface CpTutorialModalProps {
  onClose: () => void;
}

/** One rules section: a heading + a list of bullet points. Sourced from i18n. */
type TutorialSection = { h: string; items: string[] };

/**
 * Scrollable rules tutorial for Chinese Poker (Pineapple OFC + Fantasyland).
 *
 * Content lives in the `chinesepoker` i18n namespace under `tutorial.sections`
 * (en + it), so it stays localized and editable without touching this file.
 * Styled as a bottom sheet, mirroring CpQueueSheet / CpInviteSheet.
 */
export function CpTutorialModal({ onClose }: CpTutorialModalProps) {
  const { t } = useTranslation('chinesepoker');
  // i18next returns the array shape when returnObjects is set.
  const sections = (t('tutorial.sections', { returnObjects: true }) as unknown as TutorialSection[]) ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={t('tutorial.title')}
    >
      {/* Dim backdrop — tap to close */}
      <button
        type="button"
        aria-label={t('tutorial.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />

      <div className="relative flex max-h-[88vh] w-full max-w-md flex-col rounded-t-2xl bg-bg-secondary">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 pb-3 pt-4">
          <div>
            <h2 className="flex items-center gap-2 text-base font-bold text-fg">
              <BookOpen className="h-4 w-4 text-brand" aria-hidden />
              {t('tutorial.title')}
            </h2>
            <p className="mt-0.5 text-xs text-fg-muted">{t('tutorial.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('tutorial.close')}
            className="-mr-1 rounded-lg p-1 text-fg-muted transition-opacity active:opacity-70"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {sections.map((s, i) => (
            <section key={i} className="space-y-1.5">
              <h3 className="text-sm font-semibold text-fg">{s.h}</h3>
              <ul className="space-y-1">
                {s.items.map((it, j) => (
                  <li key={j} className="flex gap-2 text-[13px] leading-snug text-fg-muted">
                    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-brand/60" aria-hidden />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="border-t border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
          >
            {t('tutorial.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
